import { useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";

// Platform-aware video embed (YouTube, YouTube Shorts, TikTok, Instagram, MP4).
// Auto-detects platform from the URL if `platform` is omitted.
export function detectPlatform(url = "") {
  const u = url.toLowerCase();
  if (u.includes("youtube.com/shorts")) return "youtube_shorts";
  if (u.includes("youtube.com") || u.includes("youtu.be")) return "youtube";
  if (u.includes("tiktok.com")) return "tiktok";
  if (u.includes("instagram.com")) return "instagram";
  if (u.endsWith(".mp4") || u.endsWith(".webm")) return "mp4";
  return "unknown";
}

function ytId(url = "") {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1);
    if (u.pathname.includes("/shorts/")) return u.pathname.split("/shorts/")[1].split(/[?/]/)[0];
    return u.searchParams.get("v") || "";
  } catch { return ""; }
}
function tiktokId(url = "") {
  const m = url.match(/\/video\/(\d+)/);
  return m ? m[1] : "";
}
function instaId(url = "") {
  const m = url.match(/instagram\.com\/(?:reel|p)\/([^/?]+)/);
  return m ? m[1] : "";
}

function useScript(src, condition) {
  useEffect(() => {
    if (!condition) return;
    if (document.querySelector(`script[src="${src}"]`)) return;
    const s = document.createElement("script");
    s.src = src; s.async = true;
    document.body.appendChild(s);
  }, [src, condition]);
}

export default function VideoEmbed({ url, platform, title, thumbnail, embeddable, className = "" }) {
  const p = platform || detectPlatform(url);
  const containerRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  // Instagram still needs its embed script (no first-party iframe player).
  // TikTok is now iframe-native via /player/v1/{id} — no more embed.js reliance.
  useScript("https://www.instagram.com/embed.js", p === "instagram");

  // For portrait (Shorts / Reels / TikTok) the frame is 9:16, otherwise 16:9
  const isPortrait = p === "youtube_shorts" || p === "tiktok" || p === "instagram";
  const aspect = isPortrait ? "aspect-[9/16]" : "aspect-video";

  if (!url) {
    return (
      <div className={`${aspect} ${className} rounded-lg bg-muted flex items-center justify-center border border-dashed border-border`}>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">No video yet</span>
      </div>
    );
  }

  if (p === "youtube" || p === "youtube_shorts") {
    const id = ytId(url);
    return (
      <div className={`${aspect} ${className} relative rounded-lg overflow-hidden bg-black`}>
        <iframe
          className="absolute inset-0 w-full h-full"
          src={`https://www.youtube.com/embed/${id}?rel=0&modestbranding=1`}
          title={title || "YouTube video"}
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    );
  }

  if (p === "tiktok") {
    // TikTok first-party iframe player — plays inline without needing embed.js.
    // Falls back to a link if the id can't be parsed.
    const id = tiktokId(url);
    if (!id || embeddable === false) {
      return (
        <a href={url} target="_blank" rel="noreferrer"
           className={`${aspect} ${className} rounded-lg overflow-hidden bg-black flex items-center justify-center`}>
          <span className="text-xs uppercase tracking-widest text-background/70">Watch on TikTok ↗</span>
        </a>
      );
    }
    // Poster-first: show the TikTok thumbnail + play button, load the player on tap.
    // Avoids the black "idle" frame TikTok's player shows before interaction.
    if (thumbnail && !playing) {
      return (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          data-testid={`tiktok-poster-${id}`}
          className={`${aspect} ${className} group relative w-full rounded-lg overflow-hidden bg-black text-left`}
          aria-label={`Play ${title || "TikTok video"}`}
        >
          <img src={thumbnail} alt={title || "TikTok video"} loading="lazy"
               className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <div className="absolute inset-0 bg-black/25 group-hover:bg-black/10 transition-colors" />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="w-16 h-16 rounded-full bg-primary text-white flex items-center justify-center shadow-lg transition-transform group-hover:scale-110">
              <Play size={26} fill="currentColor" className="ml-1" />
            </span>
          </span>
        </button>
      );
    }
    return (
      <div className={`${aspect} ${className} relative rounded-lg overflow-hidden bg-black`} ref={containerRef}>
        <iframe
          className="absolute inset-0 w-full h-full"
          src={`https://www.tiktok.com/player/v1/${id}?description=0&music_info=0&native_context_menu=0${playing ? "&autoplay=1" : ""}`}
          title={title || "TikTok video"}
          loading="lazy"
          allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
          allowFullScreen
        />
        <a href={url} target="_blank" rel="noreferrer" data-testid={`tiktok-open-${id}`}
           className="absolute bottom-2 right-2 text-[10px] uppercase tracking-widest bg-black/70 text-white px-2 py-1 rounded hover:bg-primary transition-colors">
          Open on TikTok ↗
        </a>
      </div>
    );
  }

  if (p === "instagram") {
    return (
      <div className={`${aspect} ${className} rounded-lg overflow-hidden bg-black`}>
        <blockquote
          className="instagram-media"
          data-instgrm-permalink={url}
          data-instgrm-version="14"
          style={{ background: "#000", margin: 0, minWidth: "100%", width: "100%" }}
        >
          <a href={url} target="_blank" rel="noreferrer">Watch on Instagram</a>
        </blockquote>
      </div>
    );
  }

  if (p === "mp4") {
    return (
      <div className={`${aspect} ${className} rounded-lg overflow-hidden bg-black`}>
        <video className="w-full h-full object-cover" src={url} controls playsInline />
      </div>
    );
  }

  // Unknown / fallback — clickable poster
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className={`${aspect} ${className} rounded-lg overflow-hidden bg-muted border border-border flex items-center justify-center hover:bg-muted/80`}
    >
      <span className="text-xs uppercase tracking-widest text-muted-foreground">Open video ↗</span>
    </a>
  );
}
